import { useState } from "react";
import { Paper, Box, Typography, Stack, Button, Chip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { StyledTextField } from "../../../components/ui/CommonReusables";
import { generateButtonSx } from "../../../utils/elementor-style-override";

const primaryBlue = "#1D54A2";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const addButtonSx = {
	backgroundColor: `${primaryBlue} !important`,
	color: "#FFFFFF !important",
	fontWeight: 700,
	minWidth: 120,
	flexShrink: 0,
	...generateButtonSx({ bg: "#16407F !important", text: "#FFFFFF" }, { bg: "#16407F !important", text: "#FFFFFF" }),
};

const inputSx = {
	"& .MuiOutlinedInput-input": {
		border: "none !important",
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: primaryBlue,
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: primaryBlue,
		borderWidth: "1px",
	},
};

const EmailRecipientsCard = ({ title, description, emails, onChange }) => {
	const [emailInput, setEmailInput] = useState("");
	const [emailError, setEmailError] = useState("");

	const addEmail = () => {
		const trimmed = emailInput.trim();
		if (!trimmed) return;

		if (!EMAIL_PATTERN.test(trimmed)) {
			setEmailError("Enter a valid email address.");
			return;
		}
		if (emails.some((email) => email.toLowerCase() === trimmed.toLowerCase())) {
			setEmailError("That email is already on the list.");
			return;
		}

		onChange([...emails, trimmed]);
		setEmailInput("");
		setEmailError("");
	};

	const removeEmail = (email) => {
		onChange(emails.filter((existing) => existing !== email));
	};

	return (
		<Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 3, width: "100% !important" }}>
			<Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
				<MailOutlineIcon fontSize="small" sx={{ color: primaryBlue }} />
				<Typography variant="subtitle2" fontWeight={700}>
					{title}
				</Typography>
			</Stack>
			<Typography variant="body2" color="text.secondary" mb={1.5}>
				{description}
			</Typography>

			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={1.5}
				alignItems={{ sm: "flex-start" }}
				mb={emails.length ? 1.5 : 0}
				sx={{ width: "100%" }}
			>
				<StyledTextField
					fullWidth
					size="small"
					type="email"
					placeholder="e.g. admin@allyhealth.com"
					value={emailInput}
					onChange={(e) => {
						setEmailInput(e.target.value);
						if (emailError) setEmailError("");
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter") addEmail();
					}}
					error={Boolean(emailError)}
					helperText={emailError}
					sx={{ ...inputSx, flex: 1 }}
				/>
				<Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={addEmail} disabled={!emailInput.trim()} sx={addButtonSx}>
					Add
				</Button>
			</Stack>

			{emails.length > 0 && (
				<Stack direction="row" flexWrap="wrap" gap={1}>
					{emails.map((email) => (
						<Chip
							key={email}
							label={email}
							onDelete={() => removeEmail(email)}
							deleteIcon={<CloseIcon fontSize="small" />}
							sx={{
								bgcolor: "#EBF5FF",
								color: primaryBlue,
								fontWeight: 600,
								"& .MuiChip-deleteIcon": {
									color: primaryBlue,
									"&:hover": { color: "#B93544" },
								},
							}}
						/>
					))}
				</Stack>
			)}
		</Paper>
	);
};

export default EmailRecipientsCard;
